import React from 'react';
import useReveal from '../../../shared/hooks/useReveal';

const features = [
  {
    title: 'Unified Dashboard',
    description: 'Every metric, every tool, every team — one screen. Stop switching tabs. Start seeing the full picture.',
  },
  {
    title: 'Smart Integrations',
    description: 'QuickBooks, POS systems, scheduling tools, and more. Your data flows in automatically.',
  },
  {
    title: 'Real-Time Insights',
    description: 'Live data, not last month\'s report. Know what\'s happening right now, not after it\'s too late.',
  },
  {
    title: 'Predictive Analytics',
    description: 'Spot trends before they become problems. Arbcor surfaces what matters before you have to ask.',
  },
  {
    title: 'Team Visibility',
    description: 'Role-based views keep everyone aligned. The right information for the right person, always.',
  },
  {
    title: 'Scales With You',
    description: 'One location or one hundred. Arbcor grows with you. No migrations, no upgrades, no friction.',
  },
];

const Features = () => {
  const [headerRef, headerVisible] = useReveal();
  const [gridRef, gridVisible] = useReveal({ threshold: 0.05 });

  return (
    <section id="features" className="py-28 md:py-40 bg-zinc-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section number + header */}
        <div ref={headerRef}>
          <div className={`flex items-center gap-6 mb-16 reveal from-left ${headerVisible ? 'visible' : ''}`}>
            <span className="text-6xl md:text-8xl font-extralight text-zinc-200">02</span>
            <div className="h-px flex-1 bg-zinc-200" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
            <div className="lg:col-span-5">
              <h2 className={`text-3xl md:text-[2.75rem] font-light text-zinc-900 leading-tight reveal from-below ${headerVisible ? 'visible' : ''}`}
                  style={{ transitionDelay: '0.15s' }}>
                Built for how businesses actually work.
              </h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 flex items-end">
              <p className={`text-zinc-500 text-base md:text-lg leading-relaxed reveal from-below ${headerVisible ? 'visible' : ''}`}
                 style={{ transitionDelay: '0.3s' }}>
                Powerful enough for the enterprise. Simple enough for everyone else.
              </p>
            </div>
          </div>
        </div>

        {/* Feature grid - alternating layout, not a uniform grid */}
        <div ref={gridRef} className="stagger">
          {features.map((feature, i) => (
            <div
              key={i}
              className={`reveal from-below flex flex-col md:flex-row items-start gap-4 md:gap-8 py-8 border-b border-zinc-200 last:border-0 ${gridVisible ? 'visible' : ''}`}
            >
              <div className="flex items-center gap-4 md:w-64 flex-shrink-0">
                <span className="text-xs text-zinc-300 tracking-wider">0{i + 1}</span>
                <h3 className="text-lg font-medium text-zinc-900">{feature.title}</h3>
              </div>
              <p className="text-zinc-500 text-base leading-relaxed md:max-w-lg">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
